/**
 * Centralized Category Definitions, Permanent Links, and Hierarchy Tree.
 */

export const DEFAULT_CATEGORY_TREE: Record<string, string[]> = {
  "Electronics": [
    "AirPods & Earbuds",
    "Headphones & Audio",
    "Mobiles & Accessories",
    "Laptops & Computers",
    "Computer Accessories & Stands",
    "TVs & Home Entertainment",
    "Cameras & Photography",
    "Smartwatches & Wearables",
    "Gaming & Consoles",
    "Cables & Power Banks",
  ],
  "Fashion": [
    "Men's Clothing",
    "Women's Clothing",
    "Shoes & Footwear",
    "Bags & Luggage",
    "Watches & Accessories",
    "Jewelry",
  ],
  "Home & Kitchen": [
    "Kitchen & Dining",
    "Home Appliances",
    "Home Decor",
    "Bedding & Linens",
    "Furniture",
    "Storage & Organization",
  ],
  "Beauty & Personal Care": [
    "Skin Care",
    "Hair Care",
    "Fragrances & Perfumes",
    "Makeup",
    "Personal Grooming",
  ],
  "Sports & Outdoors": [
    "Exercise & Fitness",
    "Outdoor Recreation",
    "Sports Apparel",
    "Camping & Hiking",
  ],
  "Office & Stationery": [
    "Office Supplies",
    "Desk Organization",
    "Printers & Ink",
    "Stationery & Notebooks",
  ],
  "Automotive & Tools": [
    "Car Electronics & Audio",
    "Car Accessories",
    "Hand & Power Tools",
  ],
  "Toys & Baby": [
    "Baby Essentials",
    "Toys & Games",
    "Kids' Fashion",
  ],
  "Books & Media": [
    "Fiction & Literature",
    "Non-Fiction",
    "Academic & Textbooks",
  ],
};

export const PRIMARY_CATEGORIES = Object.keys(DEFAULT_CATEGORY_TREE);

export type PrimaryCategoryMeta = {
  name: string;
  icon: string;
  badge: string;
  slug: string;
  permanentLink: string;
};

export function toCategorySlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/**
 * Generates the canonical, permanent storefront link for any category.
 * Always resolves to `/search?category=${encodeURIComponent(categoryName)}`.
 */
export function getCategoryPermanentLink(nameOrSlug: string): string {
  if (!nameOrSlug?.trim()) return "/search";
  const trimmed = nameOrSlug.trim();
  if (trimmed.startsWith("/")) return trimmed;
  return `/search?category=${encodeURIComponent(trimmed)}`;
}

export const PRIMARY_CATEGORY_METAS: PrimaryCategoryMeta[] = [
  {
    name: "Electronics",
    icon: "⚡",
    badge: "Tech & Audio",
    slug: "electronics",
    permanentLink: "/search?category=Electronics",
  },
  {
    name: "Fashion",
    icon: "👗",
    badge: "Apparel & Shoes",
    slug: "fashion",
    permanentLink: "/search?category=Fashion",
  },
  {
    name: "Home & Kitchen",
    icon: "🍳",
    badge: "Home & Dining",
    slug: "home-kitchen",
    permanentLink: "/search?category=Home%20%26%20Kitchen",
  },
  {
    name: "Beauty & Personal Care",
    icon: "💄",
    badge: "Skin & Care",
    slug: "beauty-personal-care",
    permanentLink: "/search?category=Beauty%20%26%20Personal%20Care",
  },
  {
    name: "Sports & Outdoors",
    icon: "🏃",
    badge: "Fitness & Sport",
    slug: "sports-outdoors",
    permanentLink: "/search?category=Sports%20%26%20Outdoors",
  },
  {
    name: "Office & Stationery",
    icon: "💼",
    badge: "Work & Desk",
    slug: "office-stationery",
    permanentLink: "/search?category=Office%20%26%20Stationery",
  },
  {
    name: "Automotive & Tools",
    icon: "🚗",
    badge: "Car & Hardware",
    slug: "automotive-tools",
    permanentLink: "/search?category=Automotive%20%26%20Tools",
  },
  {
    name: "Toys & Baby",
    icon: "🧸",
    badge: "Kids & Baby",
    slug: "toys-baby",
    permanentLink: "/search?category=Toys%20%26%20Baby",
  },
  {
    name: "Books & Media",
    icon: "📚",
    badge: "Reading & Media",
    slug: "books-media",
    permanentLink: "/search?category=Books%20%26%20Media",
  },
];

const CATEGORY_SLUG_ALIASES: Record<string, string> = {
  phone: "Mobiles & Accessories",
  phones: "Mobiles & Accessories",
  mobile: "Mobiles & Accessories",
  mobiles: "Mobiles & Accessories",
  smartphone: "Mobiles & Accessories",
  smartphones: "Mobiles & Accessories",
  "mobiles-accessories": "Mobiles & Accessories",
  "mobiles-and-accessories": "Mobiles & Accessories",
  pc: "Laptops & Computers",
  pcs: "Laptops & Computers",
  laptop: "Laptops & Computers",
  laptops: "Laptops & Computers",
  computer: "Laptops & Computers",
  computers: "Laptops & Computers",
  desktop: "Laptops & Computers",
  desktops: "Laptops & Computers",
  "laptops-computers": "Laptops & Computers",
  "laptops-and-computers": "Laptops & Computers",
};

export function resolveCategoryFromSlug(slug: string): string {
  const normalized = slug.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-");
  if (CATEGORY_SLUG_ALIASES[normalized]) {
    return CATEGORY_SLUG_ALIASES[normalized];
  }

  const found = PRIMARY_CATEGORY_METAS.find(
    (m) => m.slug === normalized || m.slug.replace(/-/g, "") === normalized.replace(/-/g, "")
  );
  if (found) return found.name;

  for (const [main, subs] of Object.entries(DEFAULT_CATEGORY_TREE)) {
    if (toCategorySlug(main) === normalized) return main;
    for (const sub of subs) {
      if (toCategorySlug(sub) === normalized) return sub;
    }
  }

  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
